import { useRef, useState, useCallback, useMemo } from 'react';
import { withSourceTemplateId } from '@/components/shared/email/sourceTemplate';

interface UseSurveyFormStateParams {
  activity: any;
  isScheduled?: boolean;
  setIsScheduled?: (value: boolean) => void;
}

export const useSurveyFormState = ({
  activity,
  isScheduled = false,
  setIsScheduled = () => {},
}: UseSurveyFormStateParams) => {
  const [currentTab, setCurrentTab] = useState('details');
  // Store user's explicit template selection (null means no user selection yet)
  const [userSelectedTemplate, setUserSelectedTemplate] = useState<any>(null);
  const [surveyType, setSurveyType] = useState(
    activity?.survey_type || 'candidate'
  );

  // Derive the effective template: user selection takes priority, fallback to activity data
  // Note: Activity object uses candidate_comm_template (mapped from API's ats_comm_template)
  const selectedTemplate = useMemo(() => {
    // If user has explicitly selected a template, use that
    if (userSelectedTemplate !== null) {
      return userSelectedTemplate;
    }
    // Otherwise, use saved activity data (for edit mode).
    return withSourceTemplateId(activity?.candidate_comm_template);
  }, [userSelectedTemplate, activity?.candidate_comm_template]);

  const detailsRef = useRef(null);
  const formEditorRef = useRef(null);
  const scheduleRef = useRef<any>(null);

  const selectOnChange = useCallback((value: any) => {
    setUserSelectedTemplate(value);
  }, []);

  const updateSurveyTypeFromDetails = useCallback(() => {
    const detailsValues = (detailsRef?.current as any)?.values || {};
    if (detailsValues.survey_type) {
      setSurveyType(detailsValues.survey_type);
    }
  }, []);

  const handleDetailsTabSubmit = useCallback(async () => {
    await (detailsRef?.current as any)?.submitForm();
    if (!(detailsRef?.current as any)?.isValid) return false;

    updateSurveyTypeFromDetails();
    setCurrentTab('email');
    return true;
  }, [updateSurveyTypeFromDetails]);

  const handleCancelSchedule = useCallback(() => {
    setIsScheduled(false);
  }, [setIsScheduled]);

  return {
    currentTab,
    setCurrentTab,
    selectedTemplate,
    setSelectedTemplate: setUserSelectedTemplate,
    surveyType,
    setSurveyType,
    detailsRef,
    formEditorRef,
    scheduleRef,
    selectOnChange,
    updateSurveyTypeFromDetails,
    handleDetailsTabSubmit,
    handleCancelSchedule,
    isScheduled,
    setIsScheduled,
  };
};
